import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../data.js';
import { createRecord, ApiError } from '../api.js';
import { Drawer, Text, Area, Pick, Err } from '../ui.js';

// The global create. Until this existed there was no way to add anything to the
// CRM from the CRM — accounts arrived only from the seed file, contacts only
// from an Apollo reveal, and deals and tasks not at all.

type Kind = 'company' | 'contact' | 'deal' | 'task';

const KINDS: { key: Kind; label: string; blurb: string }[] = [
  { key: 'company', label: 'Account',
    blurb: 'A company you intend to work. Needs a website — enrichment is domain-keyed.' },
  { key: 'contact', label: 'Contact', blurb: 'A person at an account you already track.' },
  { key: 'deal',    label: 'Deal',    blurb: 'Real money against an account, with a close date.' },
  { key: 'task',    label: 'Task',    blurb: 'Something you owe someone, with a due date.' },
];

const STAGE_OPTS = [
  'identified', 'qualified', 'warming', 'engaged', 'meeting_set',
  'opportunity', 'won', 'lost', 'nurture', 'watch',
].map((v) => ({ value: v, label: v.replace('_', ' ') }));

const DEAL_STAGE_OPTS = ['scoping', 'discovery', 'proposal', 'negotiation', 'verbal', 'won', 'lost']
  .map((v) => ({ value: v, label: v }));

/** Every provider lookup and dedupe check keys on the bare domain, not the URL. */
function bareDomain(url: string | undefined): string | null {
  if (!url?.trim()) return null;
  return url.trim().toLowerCase()
    .replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '') || null;
}

export function CreateMenu({ onClose, initial, companyId }: {
  onClose: () => void; initial?: Kind; companyId?: string;
}) {
  const { companies } = useData();
  const nav = useNavigate();
  const [kind, setKind] = useState<Kind | null>(initial ?? null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [f, setF] = useState<Record<string, string>>({ company_id: companyId ?? '' });

  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));

  const companyOpts = (companies ?? [])
    .map((c) => ({ value: c.id, label: c.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  async function save() {
    if (!kind) return;
    setSaving(true);
    setError(null);
    try {
      if (kind === 'company') {
        // The URL is what every provider lookup keys on. 27 of the 28 seeded
        // accounts have none, which is why none of them can be enriched.
        const { record } = await createRecord<{ id: string; slug: string }>('companies', {
          name: f.name, url: f.url, domain: bareDomain(f.url),
          stage: f.stage || 'identified',
          priority: f.priority || null, sub_category: f.sub_category || null,
          gap_statement: f.gap_statement || null, source: f.source || 'added by hand',
        });
        onClose();
        nav(`/companies/${record.slug}`);
        return;
      }
      if (kind === 'contact') {
        const { record } = await createRecord<{ id: string }>('contacts', {
          company_id: f.company_id, name: f.name, title: f.title || null,
          email: f.email || null, linkedin: f.linkedin || null,
          email_status: f.email ? 'revealed' : 'unrevealed',
          source: 'added by hand',
        });
        onClose();
        nav(`/contacts/${record.id}`);
        return;
      }
      if (kind === 'deal') {
        await createRecord('deals', {
          company_id: f.company_id, name: f.name,
          stage: f.stage || 'scoping',
          amount: f.amount ? Number(f.amount) : null,
          expected_close: f.expected_close || null,
          next_step: f.next_step || null,
        });
        onClose();
        // Only leave the page when there is nowhere better to be. Creating a
        // deal FROM an account used to yank the operator to the global board
        // and discard the caller's own reload — the deal appeared two screens
        // away from where they were working.
        if (!companyId) nav('/deals');
        return;
      }
      await createRecord('tasks', {
        title: f.title, due_on: f.due_on || null,
        company_id: f.company_id || null,
      });
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setSaving(false);
    }
  }

  if (!kind) {
    return (
      <Drawer title="Create" onClose={onClose}>
        <div className="picklist">
          {KINDS.map((k) => (
            <button key={k.key} className="pickcard" onClick={() => setKind(k.key)}>
              <strong>{k.label}</strong>
              <span className="meta">{k.blurb}</span>
            </button>
          ))}
        </div>
      </Drawer>
    );
  }

  const label = KINDS.find((k) => k.key === kind)!.label;
  const ready =
    kind === 'company' ? Boolean(f.name?.trim())
    : kind === 'contact' ? Boolean(f.name?.trim() && f.company_id)
    : kind === 'deal' ? Boolean(f.name?.trim() && f.company_id)
    : Boolean(f.title?.trim());

  return (
    <Drawer
      title={`New ${label.toLowerCase()}`}
      subtitle={initial ? undefined : 'Escape to cancel'}
      onClose={onClose}
      footer={
        <>
          {!initial && (
            <button className="ghost" onClick={() => setKind(null)}>← Back</button>
          )}
          <button className="primary" disabled={!ready || saving} onClick={() => void save()}>
            {saving ? 'Saving…' : `Create ${label.toLowerCase()}`}
          </button>
        </>
      }
    >
      <Err>{error}</Err>

      {kind === 'company' && (
        <>
          <Text label="Company name" value={f.name ?? ''} autoFocus
            onChange={(e) => set('name', e.target.value)} placeholder="Payra" />
          <Text label="Website" value={f.url ?? ''} placeholder="https://www.payra.com"
            hint="Required before this account can be enriched or searched for contacts."
            onChange={(e) => set('url', e.target.value)} />
          <Text label="Category" value={f.sub_category ?? ''}
            placeholder="construction AR automation"
            onChange={(e) => set('sub_category', e.target.value)} />
          <Pick label="Stage" value={f.stage ?? 'identified'} allowEmpty={false}
            options={STAGE_OPTS} onChange={(v) => set('stage', v)} />
          <Pick label="Priority" value={f.priority ?? ''}
            options={[{ value: 'P1', label: 'P1' }, { value: 'P2', label: 'P2' },
                      { value: 'P3', label: 'P3' }]}
            onChange={(v) => set('priority', v)} />
          <Area label="Gap statement" rows={3} value={f.gap_statement ?? ''}
            hint="The sharpest sentence about why they need you. Shows on the card."
            onChange={(v) => set('gap_statement', v)} />
        </>
      )}

      {kind === 'contact' && (
        <>
          <Pick label="Account" value={f.company_id ?? ''} allowEmpty={!companyId}
            options={companyOpts} onChange={(v) => set('company_id', v)} />
          <Text label="Name" value={f.name ?? ''} autoFocus
            onChange={(e) => set('name', e.target.value)} />
          <Text label="Title" value={f.title ?? ''}
            onChange={(e) => set('title', e.target.value)} />
          <Text label="Email" type="email" value={f.email ?? ''}
            hint="Leave blank to reveal from Apollo later — that costs a credit."
            onChange={(e) => set('email', e.target.value)} />
          <Text label="LinkedIn" value={f.linkedin ?? ''}
            onChange={(e) => set('linkedin', e.target.value)} />
        </>
      )}

      {kind === 'deal' && (
        <>
          <Pick label="Account" value={f.company_id ?? ''} allowEmpty={!companyId}
            options={companyOpts} onChange={(v) => set('company_id', v)} />
          <Text label="Deal name" value={f.name ?? ''} autoFocus
            placeholder="Payra — demand build retainer"
            onChange={(e) => set('name', e.target.value)} />
          <Text label="Amount" type="number" inputMode="decimal" value={f.amount ?? ''}
            placeholder="3500" hint="Monthly or total — be consistent with yourself."
            onChange={(e) => set('amount', e.target.value)} />
          <Pick label="Stage" value={f.stage ?? 'scoping'} allowEmpty={false}
            options={DEAL_STAGE_OPTS} onChange={(v) => set('stage', v)} />
          <Text label="Expected close" type="date" value={f.expected_close ?? ''}
            onChange={(e) => set('expected_close', e.target.value)} />
          <Text label="Next step" value={f.next_step ?? ''}
            placeholder="Send the hh2 breakdown, then propose a call"
            onChange={(e) => set('next_step', e.target.value)} />
        </>
      )}

      {kind === 'task' && (
        <>
          <Text label="Task" value={f.title ?? ''} autoFocus
            onChange={(e) => set('title', e.target.value)} />
          <Text label="Due" type="date" value={f.due_on ?? ''}
            onChange={(e) => set('due_on', e.target.value)} />
          <Pick label="Account" value={f.company_id ?? ''} options={companyOpts}
            onChange={(v) => set('company_id', v)} />
        </>
      )}
    </Drawer>
  );
}
